/**
 * SosButton.js
 * ────────────
 * Floating emergency button:
 *   • 3-second long-press to confirm (prevents accidental triggers)
 *   • Captures GPS via navigator.geolocation
 *   • POSTs to /api/safety/sos
 *   • Shows confirmation modal with link to manage contacts
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, X, Phone, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/apiConfig';

const HOLD_MS = 3000;

export default function SosButton({ rideId = null }) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // {id, contacts_notified}
  const [error, setError] = useState('');
  const startRef = useRef(0);
  const tickRef = useRef(null);
  const triggeredRef = useRef(false);

  const cancelHold = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (!triggeredRef.current) setProgress(0);
  };

  const startHold = () => {
    if (busy || done) return;
    triggeredRef.current = false;
    startRef.current = Date.now();
    setError('');
    tickRef.current = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - startRef.current) / HOLD_MS) * 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(tickRef.current);
        tickRef.current = null;
        triggeredRef.current = true;
        fireSos();
      }
    }, 50);
  };

  useEffect(() => () => cancelHold(), []);

  const fireSos = async () => {
    setBusy(true);
    let lat = null, lng = null, accuracy = null;
    try {
      const pos = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error('no_geolocation'));
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true, timeout: 8000, maximumAge: 30000,
        });
      });
      lat = pos.coords.latitude;
      lng = pos.coords.longitude;
      accuracy = pos.coords.accuracy;
    } catch {
      // proceed without GPS — alert still fires
    }
    try {
      const r = await axios.post(`${API}/safety/sos`, {
        ride_id: rideId, lat, lng, accuracy,
      });
      setDone(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'sos_failed');
      setProgress(0);
      triggeredRef.current = false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="sos-button"
        onMouseDown={startHold}
        onMouseUp={cancelHold}
        onMouseLeave={cancelHold}
        onTouchStart={startHold}
        onTouchEnd={cancelHold}
        onTouchCancel={cancelHold}
        aria-label={t('sos.button', 'Notruf — 3 Sek. drücken')}
        style={{
          position: 'fixed', right: 18, bottom: 22, zIndex: 9999,
          width: 64, height: 64, borderRadius: '50%',
          background: progress > 0
            ? `conic-gradient(#dc2626 ${progress}%, #7f1d1d ${progress}% 100%)`
            : 'linear-gradient(135deg,#dc2626,#7f1d1d)',
          border: '3px solid #fff',
          boxShadow: '0 8px 24px rgba(220,38,38,0.45)',
          color: '#fff', display: 'flex', alignItems: 'center',
          justifyContent: 'center', cursor: 'pointer',
          transition: 'transform 120ms', userSelect: 'none',
          transform: progress > 0 ? 'scale(1.06)' : 'scale(1)',
        }}
      >
        <ShieldAlert size={28} />
      </button>

      {progress > 0 && progress < 100 && !busy && !done && (
        <div data-testid="sos-hint" style={hint}>
          {t('sos.holdHint', 'Halte gedrückt, um den Notruf zu senden…')}
        </div>
      )}

      {busy && (
        <div style={hint}>
          <Loader2 size={14} className="spin" /> {t('sos.sending', 'Notruf wird gesendet…')}
        </div>
      )}

      {done && (
        <div data-testid="sos-done-modal" style={modalBackdrop} onClick={() => setDone(null)}>
          <div style={modalCard} onClick={e => e.stopPropagation()}>
            <button onClick={() => setDone(null)} style={closeBtn}><X size={16} /></button>
            <div style={{ textAlign: 'center', padding: '6px 6px 12px' }}>
              <div style={iconCircle}><Check size={28} color="#fff" /></div>
              <h3 style={{ margin: '12px 0 6px', color: '#dc2626' }}>
                {t('sos.sentTitle', 'Notruf gesendet')}
              </h3>
              <p style={{ color: '#475569', fontSize: 14 }}>
                {t('sos.sentBody', '{{count}} Notfallkontakt(e) wurden benachrichtigt — Standort wurde übermittelt.', { count: done.contacts_notified || 0 })}
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <a href="tel:112" style={call112}><Phone size={18} /> {t('sos.call112', 'Direkt 112 anrufen')}</a>
              <button data-testid="sos-resolve" onClick={async () => {
                try { await axios.post(`${API}/safety/sos/${done.id}/resolve`); } catch {}
                setDone(null);
              }} style={safeBtn}>
                {t('sos.markSafe', 'Alles in Ordnung — Alarm beenden')}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div data-testid="sos-error" style={{ ...hint, background: '#7f1d1d' }}>{error}</div>
      )}
    </>
  );
}

const hint = {
  position: 'fixed', right: 18, bottom: 96, zIndex: 9999,
  background: '#1e293b', color: '#fef2f2',
  padding: '6px 12px', borderRadius: 999,
  fontSize: 12, fontWeight: 600,
  boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
  display: 'flex', alignItems: 'center', gap: 6,
};
const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.7)',
  backdropFilter: 'blur(6px)', display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  zIndex: 10000, padding: 16,
};
const modalCard = {
  position: 'relative', width: '100%', maxWidth: 380,
  background: '#fff', borderRadius: 18, padding: 22,
  boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
};
const closeBtn = {
  position: 'absolute', top: 10, right: 10,
  background: '#f1f5f9', border: 'none', borderRadius: 8,
  padding: 4, cursor: 'pointer',
};
const iconCircle = {
  width: 60, height: 60, borderRadius: '50%',
  background: 'linear-gradient(135deg,#22c55e,#15803d)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: '0 6px 18px rgba(34,197,94,0.45)',
};
const call112 = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '12px 14px', borderRadius: 12,
  background: 'linear-gradient(135deg,#dc2626,#991b1b)',
  color: '#fff', fontWeight: 700, textDecoration: 'none', fontSize: 15,
};
const safeBtn = {
  padding: '10px 14px', borderRadius: 12,
  background: '#f1f5f9', border: '1px solid #cbd5e1',
  color: '#0f172a', fontWeight: 600, cursor: 'pointer', fontSize: 14,
};
